import { notification, Spin } from 'antd';
import Button from 'jsx/common/Button';
import Fetch from 'jsx/common/util/_fetch';
import AutomationForm from 'jsx/configuration/components/AutomationForm';
import {
  cleanActions,
  cleanConditionTempSettings,
  cleanEmptyConditions,
  preConditionsStoreCleanup,
  preTriggerStoreCleanup,
  recursiveCleanup,
} from 'jsx/configuration/util/cleanup';
import {
  checkForEmptyAssetId,
  conditionsRefactor,
} from 'jsx/configuration/util/utils';
import {
  ApiAutomation,
  Flex,
  openNotificationWithIcon,
  PageContent,
  useFetch,
  useUserinfo,
} from '@mss/mss-component-library';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { FormValuesContext } from '../../../contexts/FormValuesContext';
import { useConfigurationQuery } from '../../../../index';

function EditAutomation() {
  const [t] = useTranslation(['configuration', 'common']);
  const { automationId } = useParams<{ automationId: string }>();
  const history = useHistory();
  const { url } = useRouteMatch();
  const [customReceivers] = useContext(FormValuesContext);
  const [hasPermission, , user] = useUserinfo();
  const { data: config } = useConfigurationQuery();
  const [{ data: automation, loading }, fetchAutomation] =
    useFetch<ApiAutomation>({
      url: `/api/automation/${automationId}/`,
    });

  const handleSubmit = async (val: ApiAutomation) => {
    const values =
      customReceivers?.value === ''
        ? { ...val }
        : {
            ...val,
            actions: val.actions.map((action, i) =>
              i === customReceivers?.index
                ? {
                    ...action,
                    settings: {
                      ...action.settings,
                      custom_email: customReceivers?.value,
                    },
                  }
                : action
            ),
          };

    values.actions = cleanActions(values.actions);
    recursiveCleanup(values.actions);
    recursiveCleanup(values.conditions);
    values.conditions = cleanEmptyConditions(
      cleanConditionTempSettings(values.conditions)
    );
    const refactoredConditions = conditionsRefactor(values);
    const nonEmptyAssetId = checkForEmptyAssetId(refactoredConditions);

    /*
    Lets create a copy and cleanup new object for posting so no reference to
    the original object exists.
     */
    const newItem = JSON.parse(JSON.stringify(nonEmptyAssetId));
    preTriggerStoreCleanup(newItem.trigger);
    preConditionsStoreCleanup(newItem.conditions);

    let res;
    try {
      res = await Fetch.patch(`/api/automation/${automationId}/`, {
        body: JSON.stringify(newItem),
      });

      if (res.status === 200) {
        openNotificationWithIcon(
          notification.success,
          t('Automation successfully updated'),
          ''
        );
        history.push('/configuration/automations');
      } else {
        throw new Error(`${res.status}`);
      }
    } catch {
      res?.text().then(text => {
        const error = JSON.parse(text);
        openNotificationWithIcon(
          notification.error,
          t(`Automation update failed`),
          error.message
        );
      });
    }
  };

  let disabled = '';
  let title = t('Edit automation');

  if (automation) {
    const can_edit =
      (!user?.is_superuser &&
        hasPermission('change_automation') &&
        automation.system_id === null) ||
      user?.is_superuser;
    disabled = can_edit ? '' : 'disabled';

    if (automation.system_id !== null) {
      title = t('System automation');
    }
  }

  return (
    <PageContent
      title={title}
      actions={
        !config?.unidirectional ? (
          <Button onClick={() => history.push(`${url}/log`)}>
            {t('Show logs')}
          </Button>
        ) : null
      }
    >
      {!automation && loading && (
        <Flex v="center" h="center">
          <Spin />
        </Flex>
      )}

      {automation && (
        <AutomationForm
          automation={automation}
          handleSubmit={handleSubmit}
          reinitialize
          disabled={disabled}
        />
      )}
    </PageContent>
  );
}

export default EditAutomation;
